'use client';
import { createElement, useEffect } from 'react';
import { canAccessSysPages } from '@/lib/sys-access';
import { useAppStore } from '@/stores/app-store';
import AuthScreen from '@/components/AuthScreen';
import Sidebar from '@/components/Sidebar';
import Topbar from '@/components/Topbar';
import Toast from '@/components/Toast';
import { DormOverviewPage, DormRoomsPage, DormTenantsPage, DormMeterPage, DormBillPage } from '@/components/pages/DormPages';
import OverviewPage from '@/components/pages/OverviewPage';
import PlantsPage from '@/components/pages/PlantsPage';
import MyDevicesPage from '@/components/pages/MyDevicesPage';
import DeviceOverviewPage from '@/components/pages/DeviceOverviewPage';
import DashboardPage from '@/components/pages/DashboardPage';
import SolarPage from '@/components/pages/SolarPage';
import EnergyPage from '@/components/pages/EnergyPage';
import EnvironmentPage from '@/components/pages/EnvironmentPage';
import PlantOverviewPage from '@/components/pages/PlantOverviewPage';
import MonitorPage from '@/components/pages/MonitorPage';
import ConnectivityPage from '@/components/pages/ConnectivityPage';
import MdbHealthPage from '@/components/pages/MdbHealthPage';
import CarbonPage from '@/components/pages/CarbonPage';
import AerationDoPage from '@/components/pages/AerationDoPage';
import EnergyBillPage from '@/components/pages/EnergyBillPage';
import PlantReportPage from '@/components/pages/PlantReportPage';
import MonthlyTempReportPage from '@/components/pages/MonthlyTempReportPage';
import AlarmCenterPage from '@/components/pages/AlarmCenterPage';
import FleetInsightsPage from '@/components/pages/FleetInsightsPage';
import CockpitPage from '@/components/pages/CockpitPage';
import ReportCenterPage from '@/components/pages/ReportCenterPage';
import CalibrationPage from '@/components/pages/CalibrationPage';
import PmTabs from '@/components/PmTabs';
import WaterPage from '@/components/pages/WaterPage';
import AIInsightsPage from '@/components/pages/AIInsightsPage';
import PlantSettingsPage from '@/components/pages/PlantSettingsPage';
import LocationPage from '@/components/pages/LocationPage';
import ProfilePage from '@/components/pages/ProfilePage';
import UserManagementPage from '@/components/pages/UserManagementPage';
import LicenseRenewalPage from '@/components/pages/LicenseRenewalPage';
import UserManualPage from '@/components/pages/UserManualPage';
import PMPage from '@/components/pages/PMPage';
import PMHistoryPage from '@/components/pages/PMHistoryPage';
import RoadmapPage from '@/components/pages/RoadmapPage';
import ActionPlanPage from '@/components/pages/ActionPlanPage';
import FeaturesPage from '@/components/pages/FeaturesPage';
import SystemOverviewPage from '@/components/pages/SystemOverviewPage';
import SystemDiagramPage from '@/components/pages/SystemDiagramPage';
import StrategyPage from '@/components/pages/StrategyPage';
import PackagesPage from '@/components/pages/PackagesPage';
import MyPackagePage from '@/components/pages/MyPackagePage';
import RenewalsPage from '@/components/pages/RenewalsPage';
import CustomerGroupsPage from '@/components/pages/CustomerGroupsPage';
import FarmOverviewPage from '@/components/pages/FarmOverviewPage';
import AddonLocked from '@/components/AddonLocked';
import { isAddonEnabled } from '@/lib/entitlements';
import DeviceChartOffcanvas from '@/components/DeviceChartOffcanvas';
import { useSite } from '@/components/SiteProvider';
import CustomDashboardBoundary from '@/components/CustomDashboardBoundary';
import { customDashboardFor } from '@/lib/custom-dashboards';
import { hiddenPageIds } from '@/lib/site-menu';
import { trackPage } from '@/lib/usage-tracker';

function PageContent() {
  const page = useAppStore((s) => s.page);
  const role = useAppStore((s) => s.currentUser?.role);
  const email = useAppStore((s) => s.currentUser?.email);
  // server ตัดสินมาแล้ว — ดู lib/can-manage-users.ts
  const canManageUsers = useAppStore((s) => s.dbUser?.canManageUsers === true || (!s.dbUser && s.currentUser?.role === 'admin'));
  // PM ปิดเมื่อแพ็กเกจไม่เปิดให้ — กันการเข้าตรงด้วย state ค้าง ไม่ใช่แค่ซ่อนเมนู
  const pmAllowed = useAppStore((s) => (s.dbUser ? s.dbUser.features?.pm !== false : true));
  const aiAllowed = useAppStore((s) => (s.dbUser ? s.dbUser.features?.ai !== false : true));
  const calAllowed = useAppStore((s) => (s.dbUser ? s.dbUser.features?.calibration !== false : true));
  const currentPlant = useAppStore((s) => s.currentPlant);
  const lang = useAppStore((s) => s.lang);
  const site = useSite();
  // Pages hidden for this subdomain (Site Manager). Block direct access too,
  // not just the menu entry.
  const hidden = hiddenPageIds(site.hiddenPages);
  const signedIn = useAppStore((s) => !!s.dbUser);

  // usage analytics (admin → Usage Analytics): page / plant views + visible time
  useEffect(() => {
    if (!signedIn) return;
    const pid = currentPlant != null && /^\d+$/.test(String(currentPlant)) ? Number(currentPlant) : undefined;
    trackPage(page, pid);
  }, [signedIn, page, currentPlant]);

  // Viewers have no plant picker in the sidebar. If one has a plant but none is
  // selected (e.g. a session created before auto-select, or any stale state),
  // pick their first plant and land on its dashboard — self-heals regardless of
  // how the session was established.
  useEffect(() => {
    if (role !== 'viewer' || currentPlant) return;
    const st = useAppStore.getState();
    const first = st.currentUser?.plants.find((pid) => st.plants[pid]);
    if (first) useAppStore.setState({ currentPlant: first, page: 'dashboard' });
  }, [role, currentPlant]);

  // (Viewers may now open Operation Center / All Plants — the plant list is
  // already scoped to their bound plants server-side, so the overview shows only
  // what they can access. Create/manage actions stay gated by role.)

  // Site-level page hiding: if the current page is disabled for this subdomain,
  // show a neutral notice instead of the page (defends against stale nav state
  // or a persisted page id pointing at a now-hidden page).
  if (hidden.has(page)) {
    return (
      <div className="g-panel">
        <div className="g-body" style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>
            {lang === 'th' ? 'หน้านี้ไม่พร้อมใช้งาน' : 'This page is not available'}
          </div>
          <div style={{ fontSize: 13 }}>
            {lang === 'th' ? 'ผู้ดูแลระบบได้ปิดการใช้งานหน้านี้สำหรับไซต์นี้' : 'The administrator has disabled this page for this site.'}
          </div>
        </div>
      </div>
    );
  }

  // User Management is the site Admin email's page only (Site Manager). Block
  // direct/stale-state access too, not just the sidebar entry.
  if (page === 'usermgmt' && !canManageUsers) {
    return (
      <div className="g-panel">
        <div className="g-body" style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>
            {lang === 'th' ? 'หน้านี้ไม่พร้อมใช้งาน' : 'This page is not available'}
          </div>
          <div style={{ fontSize: 13 }}>
            {lang === 'th' ? 'เฉพาะผู้ดูแลของไซต์นี้' : "Only this site's administrator can open this page."}
          </div>
        </div>
      </div>
    );
  }

  // System Overview / Diagram are restricted to the accounts in lib/sys-access.
  // Block direct/stale-state access too, not just the sidebar entry.
  if ((page === 'sysoverview' || page === 'sysdiagram' || page === 'strategy' || page === 'packages' || page === 'renewals' || page === 'custgroups') && !canAccessSysPages(email)) {
    return (
      <div className="g-panel">
        <div className="g-body" style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>
            {lang === 'th' ? 'หน้านี้ไม่พร้อมใช้งาน' : 'This page is not available'}
          </div>
          <div style={{ fontSize: 13 }}>
            {lang === 'th' ? 'หน้านี้จำกัดเฉพาะบางบัญชี' : 'This page is restricted to specific accounts.'}
          </div>
        </div>
      </div>
    );
  }

  switch (page) {
    case 'overview': return <OverviewPage />;
    case 'plants': return <PlantsPage />;
    case 'mydevices': return <MyDevicesPage />;
    case 'deviceoverview': return <DeviceOverviewPage />;
    // Subdomains listed in the custom-dashboard registry get a hand-written
    // page instead of the grid; DashboardPage never mounts, so its Edit Layout
    // / Add Widget controls disappear with it. See docs/custom-dashboard-plan.md.
    case 'dashboard': {
      // Registry entries are module-level `dynamic()` constants, so the looked-up
      // type is stable across renders; createElement keeps it out of JSX so the
      // static-components lint rule doesn't read it as a component built here.
      const custom = customDashboardFor(site.cusGroup);
      return custom
        ? <CustomDashboardBoundary th={lang === 'th'}>{createElement(custom)}</CustomDashboardBoundary>
        : <DashboardPage />;
    }
    case 'solar': return <SolarPage />;
    case 'energy': return <EnergyPage />;
    case 'env': return <EnvironmentPage />;
    case 'plantoverview': return <PlantOverviewPage />;
    case 'farmoverview': return <FarmOverviewPage />;
    case 'monitor': return <MonitorPage />;
    case 'monitor2': return <MonitorPage />;
    case 'connectivity': return <ConnectivityPage />;
    case 'mdbhealth': return <MdbHealthPage />;
    case 'carbon': return <CarbonPage />;
    case 'aeration': return <AerationDoPage />;
    case 'energybill': return <EnergyBillPage />;
    case 'plantreport': return <PlantReportPage />;
    case 'tempreport': return <MonthlyTempReportPage />;
    case 'water': return <WaterPage />;
    case 'ai':
      // AI เป็นฟีเจอร์ของแพ็กเกจจ่ายเงิน — ปิดทั้งเมนูและการเข้าตรง
      return aiAllowed ? <AIInsightsPage /> : (
        <AddonLocked
          titleTh="AI · วิเคราะห์และตรวจจับความผิดปกติ"
          titleEn="AI · Insight & Anomaly Detection"
          descTh="ไม่ต้องเปิดดูทีละไซต์เพื่อหาว่ามีอะไรผิด — ระบบไล่ดูให้ทุกไซต์แล้วบอกว่าควรไปที่ไหนก่อน พร้อมเหตุผลที่ตรวจสอบย้อนได้"
          descEn="Stop opening one site after another to find the problem — it reviews them all and tells you which one needs you first, with the reasoning shown."
          priceTh="มีในแพ็กเกจ Starter ขึ้นไป"
          priceEn="Included from Starter upward"
        />
      );
    case 'cockpit': return <CockpitPage />;
    case 'reports': return <ReportCenterPage />;
    case 'calibration':
      // สอบเทียบเป็นฟีเจอร์ของแพ็กเกจ — server ตัดสินมาใน dbUser.features
      return calAllowed ? <CalibrationPage /> : (
        <AddonLocked
          titleTh="ทะเบียนสอบเทียบ"
          titleEn="Calibration register"
          descTh="เลิกไล่วันครบกำหนดในเอ็กเซลและรื้อแฟ้มตอนออดิท — ระบบเตือนก่อนถึงกำหนด เก็บใบรับรองไว้ครบ และนำค่าชดเชยไปใช้กับค่าที่อ่านได้จริง"
          descEn="Stop tracking due dates in a spreadsheet and digging through folders at audit time — you are reminded before each one falls due, every certificate is kept, and the correction reaches the live reading."
          priceTh="มีในแพ็กเกจ Basic ขึ้นไป"
          priceEn="Included from Basic upward"
        />
      );
    case 'aifleet':
      // ใช้เกณฑ์เดียวกับหน้า AI ของไซต์ — ฟีเจอร์เดียวกัน คนละขอบเขต
      return aiAllowed ? <FleetInsightsPage /> : (
        <AddonLocked
          titleTh="AI · วิเคราะห์และตรวจจับความผิดปกติ"
          titleEn="AI · Insight & Anomaly Detection"
          descTh="ไม่ต้องเปิดดูทีละไซต์เพื่อหาว่ามีอะไรผิด — ระบบไล่ดูให้ทุกไซต์แล้วบอกว่าควรไปที่ไหนก่อน พร้อมเหตุผลที่ตรวจสอบย้อนได้"
          descEn="Stop opening one site after another to find the problem — it reviews them all and tells you which one needs you first, with the reasoning shown."
          priceTh="มีในแพ็กเกจ Starter ขึ้นไป"
          priceEn="Included from Starter upward"
        />
      );
    case 'psettings': return <PlantSettingsPage />;
    case 'location': return <LocationPage />;
    case 'alarms': return <AlarmCenterPage />;
    case 'profile': return <ProfilePage />;
    case 'usermgmt': return <UserManagementPage />;
    case 'license': return <LicenseRenewalPage />;
    case 'manual': return <UserManualPage />;
    case 'pm':
    case 'pmcal':
    case 'pmhist':
      // Preventive Maintenance is a paid add-on — not in the current package.
      if (!isAddonEnabled('maintenance') || !pmAllowed) {
        return (
          <AddonLocked
            titleTh="ระบบบำรุงรักษา (Maintenance)"
            titleEn="Preventive Maintenance"
            descTh="ไม่ต้องจำเองว่าไซต์ไหนถึงคิว — ระบบเตือนก่อนถึงกำหนด บันทึกว่าใครทำอะไรไปแล้ว และเก็บประวัติไว้ตอบลูกค้ากับออดิท"
            descEn="Stop keeping the schedule in your head — you are reminded before each visit is due, who did what is recorded, and the history is there when a customer or an auditor asks."
            priceTh="มีในแพ็กเกจ Standard ขึ้นไป"
            priceEn="Included from Standard upward"
          />
        );
      }
      return <PmTabs>{page === 'pmhist' ? <PMHistoryPage /> : <PMPage />}</PmTabs>;
    case 'roadmap': return <RoadmapPage />;
    case 'actionplan': return <ActionPlanPage />;
    case 'features': return <FeaturesPage />;
    case 'sysoverview': return <SystemOverviewPage />;
    case 'sysdiagram': return <SystemDiagramPage />;
    case 'strategy': return <StrategyPage />;
    case 'packages': return <PackagesPage />;
    case 'mypackage': return <MyPackagePage />;
    case 'renewals': return <RenewalsPage />;
    case 'custgroups': return <CustomerGroupsPage />;
    case 'dormoverview': return <DormOverviewPage />;
    case 'dormrooms': return <DormRoomsPage />;
    case 'dormtenants': return <DormTenantsPage />;
    case 'dormmeter': return <DormMeterPage />;
    case 'dormbill': return <DormBillPage />;
    default: return (
      <div className="g-panel">
        <div className="g-body" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Coming soon</div>
      </div>
    );
  }
}

export default function Home() {
  const currentUser = useAppStore((s) => s.currentUser);
  const sidebarCollapsed = useAppStore((s) => s.sidebarCollapsed);
  const mobileNavOpen = useAppStore((s) => s.mobileNavOpen);
  const setMobileNav = useAppStore((s) => s.setMobileNav);
  const authLoading = useAppStore((s) => s.authLoading);
  const restoreSession = useAppStore((s) => s.restoreSession);

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  if (authLoading) {
    return (
      <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-deep)' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          <div style={{ width: 40, height: 40, border: '3px solid var(--glass-border)', borderTopColor: 'var(--gold)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
          <div style={{ fontSize: 13 }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <AuthScreen />;
  }

  return (
    <>
      <Sidebar />
      {/* Backdrop behind the mobile nav drawer — tap to close (desktop: unused) */}
      <div
        className={`nav-backdrop${mobileNavOpen ? ' open' : ''}`}
        onClick={() => setMobileNav(false)}
      />
      <div className="main" style={sidebarCollapsed ? { marginLeft: 68 } : undefined}>
        <Topbar />
        <div className="content page-enter">
          <PageContent />
        </div>
      </div>
      <DeviceChartOffcanvas />
      <Toast />
    </>
  );
}
